import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, doc, updateDoc, getDocs } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Bell, Heart } from 'lucide-react';
import {
  getReactionsLastSeenMs,
  isFirestoreNotificationId,
  isNotificationRead,
  setReactionsLastSeenMs,
} from '../utils/reactionArtworkId';
import { API_ENDPOINTS } from '../config/backendConfig';
import { authFetch } from '../utils/authFetch';
import { toJSDate } from '../utils/date';

function serializeFeedTimestamp(value) {
  const date = toJSDate(value);
  return date ? date.toISOString() : null;
}

function feedItemTimeMs(item) {
  const date = toJSDate(item?.createdAt || item?.timestamp);
  return date ? date.getTime() : 0;
}

const REACTION_TYPES = new Set(['reaction', 'like', 'kudos']);

function reactionToFeedItem(reaction, lastSeenMs = 0) {
  if (!reaction?.userId) return null;
  const title = reaction.artworkTitle || 'your artwork';
  const createdAt = reaction.timestamp || null;
  const timeMs = feedItemTimeMs({ createdAt, timestamp: createdAt });
  return {
    id: `reaction-${reaction.id}`,
    type: 'reaction',
    reactionType: reaction.reactionType || 'like',
    senderName: reaction.userName || 'An artist',
    senderUid: reaction.userId,
    artworkId: reaction.artworkId,
    artworkTitle: title,
    message: `reacted to "${title}"`,
    createdAt,
    isRead: timeMs > 0 && timeMs <= lastSeenMs,
    source: 'reactions',
  };
}

function normalizeNotification(raw) {
  if (!raw) return null;
  const data = typeof raw === 'object' && raw.id ? raw : { ...raw };
  const type = String(data.type || '').toLowerCase();
  if (type === 'comment') return null;
  if (type && !REACTION_TYPES.has(type) && !data.reactionType) return null;

  const createdAt = data.createdAt || data.timestamp || null;
  const senderName =
    data.senderName || data.fromUserName || data.fromArtistName || 'An artist';
  const artworkTitle = data.artworkTitle || null;
  const message =
    data.message ||
    (artworkTitle
      ? `reacted to "${artworkTitle}"`
      : `${senderName} reacted to your artwork`);

  return {
    ...data,
    type: type || 'reaction',
    message,
    senderName,
    senderUid: data.senderUid || data.fromUserId || null,
    artworkTitle,
    createdAt,
    isRead: data.isRead === true || data.read === true,
    source: 'notification',
  };
}

function mergeFeedItems(notifications, reactions) {
  const byKey = new Map();
  const add = (item) => {
    if (!item) return;
    const key = `${item.senderUid || ''}:${item.artworkId || ''}:${item.reactionType || 'like'}`;
    if (!byKey.has(key)) byKey.set(key, item);
  };
  notifications.forEach(add);
  reactions.forEach(add);
  return sortNotifications(Array.from(byKey.values()));
}

function sortNotifications(list) {
  return [...list].sort((a, b) => feedItemTimeMs(b) - feedItemTimeMs(a));
}

const ReactionNotifications = ({ refreshKey = 0, variant = 'default' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);

  const applyFeed = useCallback(
    (notificationItems, reactionItems) => {
      const lastSeenMs = getReactionsLastSeenMs(user?.uid);
      const normalizedNotifs = notificationItems.map(normalizeNotification).filter(Boolean);
      const normalizedReactions = reactionItems
        .map((r) => reactionToFeedItem(r, lastSeenMs))
        .filter(Boolean);
      const merged = mergeFeedItems(normalizedNotifs, normalizedReactions)
        .slice(0, 20)
        .map((item) => ({
          ...item,
          createdAt: serializeFeedTimestamp(item.createdAt || item.timestamp),
        }));
      setNotifications(merged);
      setUnreadCount(merged.filter((n) => !isNotificationRead(n)).length);
      if (user?.uid && merged.length > 0) {
        try {
          localStorage.setItem(`reaction_notifications_${user.uid}`, JSON.stringify(merged));
        } catch (_) {}
      }
    },
    [user?.uid]
  );

  const fetchReactionsForArtist = useCallback(async () => {
    if (!user?.uid) return [];
    try {
      const response = await fetch(
        `${API_ENDPOINTS.getKudosForArtist}?artistId=${encodeURIComponent(user.uid)}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.reactions) ? data.reactions : [];
    } catch (err) {
      console.warn('[ReactionNotifications] reactions fetch failed:', err?.message || err);
      return [];
    }
  }, [user?.uid]);

  const fetchFromApi = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const [notifRes, reactions] = await Promise.all([
        authFetch(`${API_ENDPOINTS.getNotifications}?userId=${encodeURIComponent(user.uid)}`),
        fetchReactionsForArtist(),
      ]);

      let notificationItems = [];
      if (notifRes.ok) {
        const data = await notifRes.json();
        notificationItems = Array.isArray(data.notifications) ? data.notifications : [];
      }
      applyFeed(notificationItems, reactions);
    } catch (err) {
      console.warn('[ReactionNotifications] API fetch failed:', err?.message || err);
    }
  }, [user?.uid, applyFeed, fetchReactionsForArtist]);

  const fetchFromFirestore = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const queries = [
        query(collection(db, 'notifications'), where('recipientUid', '==', user.uid)),
        query(collection(db, 'notifications'), where('recipientId', '==', user.uid)),
        query(collection(db, 'notifications'), where('userId', '==', user.uid)),
      ];
      const snapshots = await Promise.all(queries.map((q) => getDocs(q)));
      const byId = new Map();
      snapshots.forEach((snap) => {
        snap.docs.forEach((d) => {
          if (!byId.has(d.id)) {
            byId.set(d.id, { id: d.id, ...d.data() });
          }
        });
      });
      const reactions = await fetchReactionsForArtist();
      applyFeed(Array.from(byId.values()), reactions);
    } catch (err) {
      console.warn('[ReactionNotifications] Firestore fetch failed:', err?.message || err);
    }
  }, [user?.uid, applyFeed, fetchReactionsForArtist]);

  // Close when other popovers open
  useEffect(() => {
    const handler = () => setIsOpen(false);
    window.addEventListener('ui-close-popovers', handler);
    return () => window.removeEventListener('ui-close-popovers', handler);
  }, []);

  // Close on click outside and Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!isOpen) return;
      const el = containerRef.current;
      const insidePortal = !!e.target.closest && e.target.closest('.reactions-dropdown');
      if (el && !el.contains(e.target) && !insidePortal) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // When opening dropdown, mark all current notifications as read
  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    const markVisibleAsRead = async () => {
      const unread = notifications.filter((n) => !isNotificationRead(n));
      if (user?.uid) setReactionsLastSeenMs(user.uid);
      if (!cancelled) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
      const firestoreUnread = unread.filter((n) => isFirestoreNotificationId(n.id));
      if (firestoreUnread.length === 0) return;
      try {
        await Promise.all(
          firestoreUnread.map((n) =>
            updateDoc(doc(db, 'notifications', n.id), { isRead: true, read: true })
          )
        );
      } catch (e) {
        console.error('Failed to mark notifications as read:', e);
      }
    };

    markVisibleAsRead();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only mark read when panel opens
  }, [isOpen]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    try {
      const cached = localStorage.getItem(`reaction_notifications_${user.uid}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const hydrated = parsed.map((item) => ({
            ...item,
            createdAt: serializeFeedTimestamp(item.createdAt || item.timestamp),
          }));
          setNotifications(hydrated);
          setUnreadCount(hydrated.filter((n) => !isNotificationRead(n)).length);
        }
      }
    } catch (_) {}

    fetchFromApi();

    const queries = [
      query(collection(db, 'notifications'), where('recipientUid', '==', user.uid)),
      query(collection(db, 'notifications'), where('userId', '==', user.uid)),
    ];

    const unsubs = queries.map((q) =>
      onSnapshot(
        q,
        () => {
          fetchFromApi().catch(() => fetchFromFirestore());
        },
        () => {
          fetchFromApi().catch(() => fetchFromFirestore());
        },
        { purpose: 'ReactionNotifications', component: 'ReactionNotifications', essential: true }
      )
    );

    const pollId = setInterval(() => {
      fetchFromApi().catch(() => fetchFromFirestore());
    }, 30000);

    return () => {
      unsubs.forEach((unsub) => {
        try {
          unsub();
        } catch (_) {}
      });
      clearInterval(pollId);
    };
  }, [user, refreshKey, fetchFromApi, fetchFromFirestore]);

  const markAsRead = async (notificationId) => {
    if (!isFirestoreNotificationId(notificationId)) return;
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true,
        read: true,
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (user?.uid) setReactionsLastSeenMs(user.uid);
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      const unreadNotifications = notifications.filter(
        (n) => !isNotificationRead(n) && isFirestoreNotificationId(n.id)
      );
      const promises = unreadNotifications.map((notif) =>
        updateDoc(doc(db, 'notifications', notif.id), { isRead: true, read: true })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = toJSDate(timestamp);
    if (!date || Number.isNaN(date.getTime())) return 'Recently';

    const now = Date.now();
    const diffInMs = now - date.getTime();
    if (diffInMs < 0) return 'Just now';

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
    return formatted === 'Invalid Date' ? 'Recently' : formatted;
  };

  const getReactionEmoji = (reactionType) => {
    switch (reactionType) {
      case 'sparkles':
        return '✨';
      case 'bravo':
        return '👏';
      case 'heartburst':
        return '💖';
      case 'highfive':
        return '🙌';
      case 'applause':
        return '👏';
      case 'like':
        return '👍';
      default:
        return '🌟';
    }
  };

  if (!user) return null;

  const isNavPill = variant === 'nav-pill';
  const Icon = isNavPill ? Bell : Heart;
  const buttonClass = isNavPill
    ? 'dashboard-nav-pill relative'
    : 'relative inline-flex items-center justify-center gap-0.5 md:gap-2 p-1 md:px-3 md:py-1.5 rounded-full bg-white/80 text-amber-800 border border-amber-200/50 shadow-sm hover:bg-amber-50 transition-all';
  const iconClass = isNavPill
    ? 'text-amber-600'
    : 'w-2 h-2 md:w-5 md:h-5 text-amber-500 fill-amber-500 flex-shrink-0';

  return (
    <div className="relative z-[90]" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent('ui-close-popovers', { detail: 'reactions' }));
          setIsOpen((prev) => !prev);
        }}
        className={buttonClass}
        title="Artwork reactions"
        aria-label={
          unreadCount > 0
            ? `${unreadCount} new reaction${unreadCount === 1 ? '' : 's'} on your artwork`
            : 'Artwork reactions'
        }
      >
        <Icon className={iconClass} aria-hidden fill={isNavPill ? 'none' : undefined} />
        <span className={isNavPill ? 'hidden sm:inline' : 'hidden md:inline text-[11px] font-medium tracking-wide'}>
          Reactions
        </span>
        {unreadCount > 0 && (
          <span
            className={
              isNavPill
                ? 'absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-none'
                : 'absolute -top-0.5 -right-0.5 md:relative md:top-0 md:right-0 md:ml-1 inline-flex items-center justify-center min-w-[0.625rem] md:min-w-[1.25rem] h-2 md:h-4 px-0.5 md:px-1.5 rounded-full bg-amber-400 text-white text-[6px] md:text-[10px] font-semibold'
            }
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            className="reactions-dropdown fixed right-2 md:right-4 top-16 md:top-20 w-[calc(100vw-1rem)] md:w-80 max-w-sm bg-white rounded-2xl shadow-xl border border-amber-100 z-[9999] max-h-96 overflow-y-auto"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-amber-100 rounded-t-2xl bg-amber-50/60">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-amber-900">Reactions from friends</h3>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllAsRead}
                    className="text-[11px] text-amber-700 hover:text-amber-900"
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            <div className={`${expanded ? 'max-h-80 overflow-y-auto' : ''}`}>
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="text-4xl mb-2">🎨</div>
                  <p>No reactions yet</p>
                  <p className="text-sm">Share your artwork to get reactions!</p>
                </div>
              ) : (
                (expanded ? notifications : notifications.slice(0, 5)).map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors select-none ${
                      !isNotificationRead(notification) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                      markAsRead(notification.id);
                      setNotifications((prev) => {
                        const next = prev.map((n) =>
                          n.id === notification.id ? { ...n, isRead: true } : n
                        );
                        setUnreadCount(next.filter((n) => !isNotificationRead(n)).length);
                        return next;
                      });
                      if (
                        notification.book &&
                        notification.activityCode &&
                        notification.submissionId
                      ) {
                        navigate(
                          `/gallery/studio?book=${encodeURIComponent(notification.book)}&activityCode=${encodeURIComponent(notification.activityCode)}&submissionId=${encodeURIComponent(notification.submissionId)}`
                        );
                      } else if (notification.activityId && notification.submissionId) {
                        navigate(
                          `/daily-art-activity?activityId=${encodeURIComponent(notification.activityId)}`
                        );
                      } else if (notification.artworkId || notification.relatedId) {
                        navigate('/artist-wall');
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">{getReactionEmoji(notification.reactionType)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">
                          {notification.senderUid ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/artist-public-gallery/${notification.senderUid}`);
                              }}
                              className="font-medium text-amber-700 hover:underline"
                            >
                              {notification.senderName}
                            </button>
                          ) : (
                            <span className="font-medium text-amber-700">
                              {notification.senderName}
                            </span>
                          )}{' '}
                          <span className="text-gray-600">
                            reacted to{' '}
                            <span className="font-medium text-gray-700">
                              {notification.artworkTitle
                                ? `"${notification.artworkTitle}"`
                                : 'your artwork'}
                            </span>
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTimeAgo(notification.createdAt || notification.timestamp)}
                        </p>
                      </div>
                      {!isNotificationRead(notification) && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 5 && (
              <div className="p-3 border-t border-gray-200 text-center">
                {!expanded ? (
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    See more
                  </button>
                ) : (
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setExpanded(false)}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Show less
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ReactionNotifications;
